import SidebarLayout from "components/Layout/Layout";
import { useEffect, useState } from "react";
import type { CollapseProps } from "antd";
import { Button, Collapse, Dropdown, Modal, Tag } from "antd";
import { API_URL } from "env";
import PermissionEditModal from "./PemissionEditModal";
import PermissionAddModal from "./PermissionAddModal";
import { DotsThreeOutlineIcon, LockIcon, PlusIcon } from "@phosphor-icons/react";

type RoleDisplay = {
    id: string;
    displayName: string;
};

type Permission = {
    id: string;
    displayName: string;
    name: string;
    module: string;
    action: string;
    moduleDisplayName: string;
    actionDisplayName: string;
    roles_display: RoleDisplay[];
};

const ROLE_COLORS = ['purple', 'cyan', 'volcano', 'geekblue', 'gold', 'lime', 'magenta'];

const ACTION_COLOR: Record<string, string> = {
    read: 'blue',
    create: 'green',
    update: 'orange',
    delete: 'red',
};

const PermissionPage = () => {
    const [permissions, setPermissions] = useState<Permission[]>([]);
    const [viewStyle, setViewStyle] = useState<"list" | "grid">("list");
    const [isModalCreateOpen, setIsModalCreateOpen] = useState(false);
    const [isModalEditOpen, setIsModalEditOpen] = useState(false);
    const [editingPermission, setEditingPermission] = useState<string[]>([]);

    async function fetchPermissions() {
        try {
            const response = await fetch(`${API_URL}api/v1/be/permissions`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });
            if (!response.ok) throw new Error("Network response was not ok");
            const data = await response.json();
            const perms: Permission[] = data.data.items.map((item: any) => ({
                id: String(item.id),
                displayName: item.display_name,
                name: item.name,
                module: item.module ?? "",
                action: item.action,
                moduleDisplayName: item.module_display ?? "",
                actionDisplayName: item.action_display ?? "",
                roles_display: Array.isArray(item.roles_display)
                    ? item.roles_display.map((role: any) => ({ id: String(role.id), displayName: role.display_name }))
                    : [],
            }));
            setPermissions(perms);
        } catch (error) {
            console.error("Error fetching permissions:", error);
        }
    }

    useEffect(() => {
        fetchPermissions();
    }, []);

    const groupedPermissions = Object.entries(
        permissions.reduce<Record<string, Permission[]>>((acc, permission) => {
            const key = permission.moduleDisplayName || permission.module || "Unknown Module";
            if (!acc[key]) acc[key] = [];
            acc[key].push(permission);
            return acc;
        }, {})
    );

    const panels: CollapseProps["items"] = groupedPermissions.map(([module, perms]) => ({
        key: module,
        label: (
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <LockIcon size={15} className="text-text-muted" />
                    <span className="text-sm font-semibold">{module}</span>
                    <Tag className="text-xs m-0">{perms.length}</Tag>
                </div>
                <Dropdown
                    menu={{
                        items: [
                            {
                                key: "edit",
                                label: "Edit Module",
                                onClick: e => {
                                    e.domEvent?.stopPropagation();
                                    setEditingPermission(perms.map(p => p.id));
                                    setIsModalEditOpen(true);
                                },
                            },
                        ],
                    }}
                    trigger={["click"]}
                >
                    <button
                        className="p-1 rounded hover:bg-bg-hover transition-colors text-text-muted hover:text-text-primary"
                        onClick={e => e.stopPropagation()}
                    >
                        <DotsThreeOutlineIcon size={16} />
                    </button>
                </Dropdown>
            </div>
        ),
        children: <ModulePermissions perms={perms} viewStyle={viewStyle} />,
    }));

    return (
        <SidebarLayout>
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Button type="primary" icon={<PlusIcon size={14} />} onClick={() => setIsModalCreateOpen(true)}>
                            Add Permission
                        </Button>
                    </div>
                    <div className="flex border border-border overflow-hidden rounded">
                        <button
                            className={`px-3 py-1.5 text-sm transition-colors ${viewStyle === "list" ? "bg-blue-500 text-white" : "text-text-secondary hover:bg-bg-hover"}`}
                            onClick={() => setViewStyle("list")}
                        >
                            List
                        </button>
                        <button
                            className={`px-3 py-1.5 text-sm transition-colors ${viewStyle === "grid" ? "bg-blue-500 text-white" : "text-text-secondary hover:bg-bg-hover"}`}
                            onClick={() => setViewStyle("grid")}
                        >
                            Grid
                        </button>
                    </div>
                </div>

                <Collapse items={panels} accordion />
            </div>

            <Modal
                title="Add New Permission"
                open={isModalCreateOpen}
                onCancel={() => setIsModalCreateOpen(false)}
                footer={null}
                style={{ top: 50 }}
            >
                <PermissionAddModal onClose={() => { setIsModalCreateOpen(false); fetchPermissions(); }} />
            </Modal>

            <Modal
                title="Edit Permission"
                open={isModalEditOpen}
                onCancel={() => setIsModalEditOpen(false)}
                footer={null}
                style={{ top: 50 }}
            >
                <PermissionEditModal id={editingPermission} onClose={() => { setIsModalEditOpen(false); fetchPermissions(); }} />
            </Modal>
        </SidebarLayout>
    );
};

const ModulePermissions = ({ perms, viewStyle }: { perms: Permission[]; viewStyle: "grid" | "list" }) => {
    return (
        <div className={viewStyle === "grid" ? "grid grid-cols-1 md:grid-cols-2 gap-2" : "flex flex-col gap-2"}>
            {perms.map(permission => (
                <div key={permission.id} className="p-4 border border-border rounded-lg bg-bg-secondary flex flex-col gap-1">
                    <div className="flex items-start justify-between gap-2">
                        <div>
                            <span className="text-sm font-semibold">{permission.displayName}</span>
                            <span className="text-xs text-text-muted ml-1.5">#{permission.id}</span>
                        </div>
                        <Tag color={ACTION_COLOR[permission.action] ?? 'default'} className="text-xs m-0 capitalize shrink-0">
                            {permission.actionDisplayName || permission.action}
                        </Tag>
                    </div>
                    <p className="text-sm text-text-primary m-0">
                        {permission.moduleDisplayName} · {permission.actionDisplayName}
                    </p>
                    <div className="flex flex-col gap-1.5">
                        <p className="text-sm font-medium text-text-secondary m-0">Assigned Roles</p>
                        {permission.roles_display.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                                {permission.roles_display.map((role, i) => (
                                    <Tag key={role.id} color={ROLE_COLORS[i % ROLE_COLORS.length]} className="text-lg m-0">{role.displayName}</Tag>
                                ))}
                            </div>
                        ) : (
                            <span className="text-sm text-text-muted">No roles assigned</span>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default PermissionPage;
